import React from 'react';
import { useExperiment, UseExperimentResult } from '@sm/webassets';
import { PollingExperiment, PollingExperiments } from './StandardEndpage/pollingExperiments';
import { WeightedElement } from './constants';

import StandardEndpage from './StandardEndpage';

/**
 * Using a weighted distribution, select and return the page that the target element falls on
 *
 * @param weightedElements object of pages
 * @param target value within distribution range, use a random number [0, 1) if weighted distribution adds up to 1
 * @returns Selected page to serve
 */
export default function WeightedElementSelector(
  weightedElements: WeightedElement[],
  target: number
): React.ReactElement {
  let cumulativeWeight = 0.0;
  let selectedPage = <StandardEndpage pageName="CxEndpage" utSource="survey-thanks" />;

  weightedElements.every(element => {
    cumulativeWeight += element.weight;
    if (target < cumulativeWeight) {
      selectedPage = element.page;
      return false;
    }
    return true;
  });

  return selectedPage;
}

export function checkWeightsEqualToOne(weightedElements: WeightedElement[]): boolean {
  let total = 0.0;
  weightedElements.forEach(element => {
    total += element.weight;
  });
  if (Number(total.toFixed(3)) === 1) return true;
  return false;
}

export type PollingObject = {
  pageName: string;
  treatmentsObject: UseExperimentResult | undefined;
  treatmentName: string | undefined;
};

/**
 * Iterates through the experiments listed in PollingExperiments object, calls useExperiment to find active experiments
 * @returns a Polling Object which contains relevant info to determine which experiment the page has, if any
 */
export function checkForPollingExperiment(): PollingObject {
  let pageName = '';
  let treatmentsObject;
  let treatmentName;

  PollingExperiments.every((element: PollingExperiment) => {
    const treatments = useExperiment({
      name: element.experimentName,
      treatmentNames: [],
    });

    if (treatments.name) {
      pageName = element.pageName;
      treatmentsObject = treatments;
      [treatmentName] = Object.keys(treatments.treatments);
      return false;
    }
    return true;
  });
  return { pageName, treatmentsObject, treatmentName };
}

export function renderPolling(alternate: React.ReactElement): React.ReactElement {
  let toReturn = alternate;
  const { pageName, treatmentsObject, treatmentName } = checkForPollingExperiment();

  if (treatmentsObject) {
    toReturn = (
      <StandardEndpage
        pageName={pageName}
        treatments={treatmentsObject}
        treatmentName={treatmentName}
        utSource="survey-thanks"
      />
    );
  }
  return toReturn;
}
